import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CatalogQuickFilter, CatalogRow, CatalogTableColumn } from '@shared-lib/models/domain-catalog.models';

interface ColumnResizeState {
  pointerId: number;
  columnKey: string;
  neighbourKey: string | null;
  startX: number;
  startWidth: number;
  neighbourStartWidth: number;
  tableStartWidth: number;
}

@Component({
  selector: 'app-catalog-table',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './catalog-table.component.html',
  styleUrl: './catalog-table.component.scss',
})
export class CatalogTableComponent implements OnChanges {
  private static readonly MIN_COLUMN_WIDTH = 72;

  @Input() title = '';
  @Input() addButtonLabel = 'Add Row';
  @Input() columns: CatalogTableColumn[] = [];
  @Input() rows: CatalogRow[] = [];
  @Input() loading = false;
  @Input() selectedRowId: string | null = null;
  @Input() editingIds = new Set<string>();
  @Input() editValues = new Map<string, CatalogRow>();
  @Input() pendingDeleteIds = new Set<string>();
  @Input() pendingInsertIds = new Set<string>();
  @Input() pendingToggleIds = new Set<string>();
  @Input() pendingChangeCount = 0;
  @Input() isAdding = false;
  @Input() draftRow: CatalogRow | null = null;
  @Input() draftSelectOptions: Record<string, string[]> = {};
  @Input() uiConfig: any = null;
  /** Optional dropdown filter row (plus free-text search) under the table header. */
  @Input() quickFilters: CatalogQuickFilter[] = [];
  @Input() filterValues: Record<string, string> = {};

  @Output() filterChange = new EventEmitter<{ key: string; value: string }>();
  @Output() resetFilters = new EventEmitter<void>();
  @Output() rowClick = new EventEmitter<string>();
  @Output() addRow = new EventEmitter<void>();
  @Output() commitAdd = new EventEmitter<void>();
  @Output() cancelAdd = new EventEmitter<void>();
  @Output() editToggle = new EventEmitter<CatalogRow>();
  @Output() activeToggle = new EventEmitter<CatalogRow>();
  @Output() deleteRow = new EventEmitter<string>();
  @Output() saveAll = new EventEmitter<void>();
  @Output() discardChanges = new EventEmitter<void>();

  columnWidths: Record<string, number> = {};
  resizedTableWidth: number | null = null;
  resizingColumnKey: string | null = null;
  private resizeState: ColumnResizeState | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.columnWidths = {};
      this.resizedTableWidth = null;
      this.resizeState = null;
      this.resizingColumnKey = null;
    }
  }

  getColumnWidth(column: CatalogTableColumn): string | null {
    const resizedWidth = this.columnWidths[column.key];
    return resizedWidth ? `${resizedWidth}px` : column.width || null;
  }

  startColumnResize(event: PointerEvent, column: CatalogTableColumn): void {
    if (event.button !== 0) return;

    const handle = event.currentTarget as HTMLElement | null;
    const header = handle?.closest('th');
    const table = header?.closest('table');
    if (!header || !table) return;

    event.preventDefault();
    event.stopPropagation();

    const headers = Array.from(table.querySelectorAll<HTMLTableCellElement>('thead th[data-column-key]'));
    const renderedWidths: Record<string, number> = {};
    headers.forEach((item) => {
      const key = item.dataset['columnKey'];
      if (key) renderedWidths[key] = item.getBoundingClientRect().width;
    });
    this.columnWidths = renderedWidths;

    const columnIndex = this.columns.findIndex((item) => item.key === column.key);
    // The column on the right absorbs the change while it has room. For the
    // final data column the table itself changes width, leaving Actions fixed.
    const neighbour = this.columns[columnIndex + 1] || null;

    this.resizedTableWidth = table.getBoundingClientRect().width;
    this.resizingColumnKey = column.key;
    this.resizeState = {
      pointerId: event.pointerId,
      columnKey: column.key,
      neighbourKey: neighbour?.key || null,
      startX: event.clientX,
      startWidth: renderedWidths[column.key] || header.getBoundingClientRect().width,
      neighbourStartWidth: neighbour ? renderedWidths[neighbour.key] : 0,
      tableStartWidth: this.resizedTableWidth,
    };
  }

  @HostListener('window:pointermove', ['$event'])
  onColumnResize(event: PointerEvent): void {
    const state = this.resizeState;
    if (!state || event.pointerId !== state.pointerId) return;

    event.preventDefault();
    const requestedWidth = Math.max(
      CatalogTableComponent.MIN_COLUMN_WIDTH,
      state.startWidth + event.clientX - state.startX,
    );
    const widthDelta = requestedWidth - state.startWidth;
    let tableGrowth = widthDelta;

    this.columnWidths = {
      ...this.columnWidths,
      [state.columnKey]: requestedWidth,
    };

    if (state.neighbourKey) {
      const neighbourWidth = Math.max(
        CatalogTableComponent.MIN_COLUMN_WIDTH,
        state.neighbourStartWidth - widthDelta,
      );
      this.columnWidths[state.neighbourKey] = neighbourWidth;
      tableGrowth = widthDelta + neighbourWidth - state.neighbourStartWidth;
    }

    this.resizedTableWidth = state.tableStartWidth + tableGrowth;
  }

  @HostListener('window:pointerup', ['$event'])
  finishColumnResize(event: PointerEvent): void {
    if (!this.resizeState || event.pointerId !== this.resizeState.pointerId) return;
    this.resizeState = null;
    this.resizingColumnKey = null;
  }

  @HostListener('window:blur')
  cancelColumnResize(): void {
    this.resizeState = null;
    this.resizingColumnKey = null;
  }

  trackById(_: number, row: CatalogRow): string {
    return row.id;
  }

  hasDraftSelectOptions(column: CatalogTableColumn): boolean {
    return this.getSelectOptions(column).length > 0;
  }

  hasEditSelectOptions(column: CatalogTableColumn): boolean {
    if (column.key === 'pdc') {
      return false;
    }
    return this.hasDraftSelectOptions(column);
  }

  getSelectOptions(column: CatalogTableColumn): string[] {
    const dynamicOptions = this.draftSelectOptions[column.key];
    if (Array.isArray(dynamicOptions) && dynamicOptions.length > 0) {
      return dynamicOptions;
    }
    return Array.isArray(column.options) ? column.options : [];
  }

  displayValue(row: CatalogRow, column: CatalogTableColumn): unknown {
    const source = (this.editingIds.has(row.id) && this.editValues.get(row.id)) || row;
    const value = source[column.key];
    if (column.type === 'date' && typeof value === 'string') {
      const match = value.match(/^\d{4}-\d{2}-\d{2}/);
      if (match) {
        return match[0];
      }
    }
    return value;
  }

  cellTooltip(row: CatalogRow, column: CatalogTableColumn): string | null {
    if (column.key !== 'description' || this.editingIds.has(row.id)) {
      return null;
    }
    const value = this.displayValue(row, column);
    return value === null || value === undefined ? null : String(value);
  }

  isPendingDelete(row: CatalogRow): boolean {
    return this.pendingDeleteIds.has(row.id);
  }

  /** Whether the row carries any unsaved change (new, edited, toggled, or deleted). */
  isPendingRow(row: CatalogRow): boolean {
    return (
      this.pendingInsertIds.has(row.id) ||
      this.pendingToggleIds.has(row.id) ||
      this.pendingDeleteIds.has(row.id) ||
      this.editingIds.has(row.id)
    );
  }

  /** Short badge label describing why a row is pending; null when it has no pending change. */
  pendingBadgeLabel(row: CatalogRow): string | null {
    if (this.pendingInsertIds.has(row.id)) return 'New';
    if (this.pendingDeleteIds.has(row.id)) return 'Delete';
    if (this.editingIds.has(row.id) || this.pendingToggleIds.has(row.id)) return 'Edited';
    return null;
  }

  isFirstColumn(column: CatalogTableColumn): boolean {
    return !!this.columns.length && column.key === this.columns[0].key;
  }

  isRowActive(row: CatalogRow): boolean {
    const source = (this.editingIds.has(row.id) && this.editValues.get(row.id)) || row;
    return source.active !== false;
  }
}
